import React, { useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  PRODUCT_IDS,
  useCurrentProjectId,
  useCurrentProductId,
  useUser,
  useCurrentAccount,
  useProjectEntitlements,
} from '@adsk/alloy-provider';
import {
  useNotifications,
  NOTIFICATION_STATUSES,
} from '@adsk/alloy-react-notification';
import { LinkButton } from '@adsk/alloy-react-button';
import { useLocale } from '@adsk/alloy-react-locale';
import { useDataApi } from '@adsk/alloy-helpers';
import { useFormikContext } from 'formik';
import {
  ID_FIELD,
  KEY_FIELD,
  OP_STATUSES,
  SERVICE_ACCESSES,
  TRACK_EVENTS,
  FLAG_NAMES,
  mapToField,
} from '@adsk/acs-admin-ui-components-helpers';
import { useAdminFeatureFlag } from '@adsk/acs-admin-ui-components-shared-components';
import {
  buildGetProjectEntitlementsUrl,
  buildRemoveMemberRequest,
  buildUpdateMemberRequest,
  getMemberProductsFromServices,
} from '../../utils';
import {
  useSendAnalytics,
  useProductsTools,
  useProductsSubscriptions,
} from '../../hooks';
import { userPropType } from '../../propTypes';
import {
  getDocsOffAccessForAdmin,
  getMemberProductsAccessesFromProducts,
  getMemberProductsAccessesFromServices,
  getUpdatedMemberAccess,
  projectProductsUpdated,
} from './utils';
import ProductsSectionView from './component';

const {
  ADMIN_FLAGS: {
    ACC_REPLACE_WRITE_CONSTRUCTION_APIS,
    ACC_USE_PROJECT_ENTITLEMENTS_V2,
    ENABLE_PROJECT_ROLES_PRODUCTS,
  },
} = FLAG_NAMES;

const ProductsSection = ({
  isEmsAccount,
  member,
  onRemove,
  onSaveData,
  permissionsEditState,
  products,
  showProductsSectionRedesign,
}) => {
  const { value: useWriteConstructionApis } = useAdminFeatureFlag(
    ACC_REPLACE_WRITE_CONSTRUCTION_APIS
  );
  const { value: useProjectEntitlementsV2 } = useAdminFeatureFlag(
    ACC_USE_PROJECT_ENTITLEMENTS_V2
  );
  const { value: enableProjectRolesProducts } = useAdminFeatureFlag(
    ENABLE_PROJECT_ROLES_PRODUCTS
  );
  const [productsPermissions, setProductsPermissions] = useState([]);
  const [showTurnOffDocsConfirmation, setTurnOffDocsConfirmation] =
    useState(false);
  const [expandedProduct, setExpandedProduct] = useState();
  const [memberProductsAccess, setMemberProductsAccess] = useState({});
  const [showProductsBanner, setShowProductsBanner] = useState(false);
  const { setFieldValue, values } = useFormikContext();
  const {
    member: {
      selectedCompany,
      selectedRoles,
      selectedAccessLevel,
      services: memberServices,
      products: memberProducts,
    },
    projectServices,
  } = values;
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const { showNotification } = useNotifications();
  const projectId = useCurrentProjectId();
  const { currentProject, setCurrentProject } = useProjectEntitlements();
  const isAccountAdmin = currentProject?.accessLevels?.accountAdministrator;
  const currentAccount = useCurrentAccount();
  const currentProductId = useCurrentProductId();
  const userData = useUser();
  const currentUser = userData?.user;
  const purchasedProducts = productsPermissions.filter(
    (product) => product.included
  );
  const unpurchansedProducts = productsPermissions.filter(
    (product) => !product.included
  );
  const projectMemberProductIds = useMemo(
    () => mapToField(memberProducts, KEY_FIELD),
    [memberProducts]
  );
  const productsTools = useProductsTools({
    hasAccessToBuild: memberProductsAccess[PRODUCT_IDS.BUILD],
  });
  const productsSubscriptions = useProductsSubscriptions(
    member,
    products,
    showProductsSectionRedesign,
    projectMemberProductIds
  );
  const sendAnalytics = useSendAnalytics({
    memberId: member?.id,
    selectedAccessLevel,
    selectedCompany,
    selectedRoles,
  });

  const {
    data: updateMemberData,
    error: updateMemberError,
    doFetch: doUpdateMemberFetch,
    loading: isUpdatingMemberAccess,
  } = useDataApi();
  const {
    data: removeMemberData,
    loading: isRemovingMember,
    error: removeMemberError,
    doFetch: doFetchRemoveMember,
  } = useDataApi();
  const { data: projectEntitlementsData, doFetch: doFetchProjectEntitlements } =
    useDataApi();

  const isTurningOffDocs = isRemovingMember || isUpdatingMemberAccess;

  const latestOnRemoveMember = useRef(onRemove);
  const lastUpdatedProduct = useRef();
  const productsToUpdate = useRef([]);

  const handleProductsBannerClose = () => {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.delete('openCurrentUserProfilePanel');
    window.history.replaceState(
      {},
      document.title,
      `${window.location.pathname}${queryParams.toString()}`
    );
    setShowProductsBanner(false);
  };

  const updateMemberAccess = (access) => {
    const requestBody = {
      companyId: selectedCompany ? selectedCompany.id : '',
      roleIds: mapToField(selectedRoles, ID_FIELD),
      [useWriteConstructionApis ? 'products' : 'services']: access,
    };
    doUpdateMemberFetch(
      ...buildUpdateMemberRequest(
        projectId,
        member.id,
        requestBody,
        useWriteConstructionApis
      )
    );
  };

  const processProductAccessChange = (type, activated, prevMemberAccess) => {
    const access = getUpdatedMemberAccess(
      prevMemberAccess ||
        (useWriteConstructionApis ? memberProducts : memberServices),
      projectServices,
      selectedAccessLevel,
      type,
      activated,
      useWriteConstructionApis,
      projectMemberProductIds
    );
    lastUpdatedProduct.current = {
      type,
      name: products.find((p) => p.type === type).name,
      on: activated,
    };
    updateMemberAccess(access);
  };

  const handleProductAccessChange = (type, activated) => {
    if (type === PRODUCT_IDS.DOCS && !activated) {
      setTurnOffDocsConfirmation(true);
      return;
    }

    const productsAccess = { ...memberProductsAccess };
    productsAccess[type] = activated;
    setMemberProductsAccess(productsAccess);
    if (showProductsSectionRedesign && activated) setExpandedProduct(type);

    if (isUpdatingMemberAccess) {
      productsToUpdate.current.push({ type, activated });
    } else {
      processProductAccessChange(type, activated);
    }
  };

  const handleTurnOffDocsConfirmationClick = () => {
    if (selectedAccessLevel.value === SERVICE_ACCESSES.ADMIN) {
      const access = getDocsOffAccessForAdmin(
        projectServices,
        memberServices,
        useWriteConstructionApis,
        projectMemberProductIds
      );
      lastUpdatedProduct.current = {
        type: PRODUCT_IDS.DOCS,
        name: products.find((p) => p.type === PRODUCT_IDS.DOCS).name,
        on: false,
      };
      updateMemberAccess(access);
    } else {
      doFetchRemoveMember(
        ...buildRemoveMemberRequest(
          projectId,
          member.id,
          useWriteConstructionApis
        )
      );
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('openCurrentUserProfilePanel') === 'true')
      setShowProductsBanner(true);
  }, []);

  useEffect(() => {
    latestOnRemoveMember.current = onRemove;
  }, [onRemove]);

  useEffect(() => {
    const productsAccess = !member.services
      ? getMemberProductsAccessesFromProducts(member.products)
      : getMemberProductsAccessesFromServices(member.services);

    setMemberProductsAccess(productsAccess);

    if (productsAccess[currentProductId] && !expandedProduct) {
      setExpandedProduct(currentProductId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member, currentProductId]);

  useEffect(() => {
    const productsInfo = products
      .filter(
        (product) =>
          (product.userAssignable || product.type === PRODUCT_IDS.INSIGHT) &&
          projectMemberProductIds.some(
            (memberProductId) => memberProductId === product.type
          )
      )
      .map((product) => ({
        ...product,
        on: memberProductsAccess[product.type],
        tools: productsTools[product.type],
        disabled: product.type === PRODUCT_IDS.INSIGHT,
        expanded: expandedProduct === product.type,
        subscriptionInfo: productsSubscriptions[product.type],
      }));
    const currentProductInfo = productsInfo.find(
      (p) => p.type === currentProductId
    );
    const sortedProductsInfo = currentProductInfo
      ? [
          currentProductInfo,
          ...productsInfo.filter((p) => p.type !== currentProductId),
        ]
      : productsInfo;

    setProductsPermissions(sortedProductsInfo);
  }, [
    currentProductId,
    products,
    memberProductsAccess,
    productsTools,
    productsSubscriptions,
    expandedProduct,
    projectMemberProductIds,
  ]);

  useEffect(() => {
    if (!updateMemberData) return;

    if (useWriteConstructionApis) {
      setFieldValue('member.products', updateMemberData.products);
    } else {
      setFieldValue('member.services', updateMemberData.services);
    }

    const product = lastUpdatedProduct.current;
    showNotification({
      status: NOTIFICATION_STATUSES.SUCCESS,
      message: T.translate(
        product.on ? 'PRODUCT_TURNED_ON_SUCCESS' : 'PRODUCT_TURNED_OFF_SUCCESS',
        { productName: product.name }
      ),
    });

    const response = {
      status: OP_STATUSES.SUCCESS,
      response: useWriteConstructionApis
        ? updateMemberData
        : {
            ...updateMemberData,
            products: getMemberProductsFromServices(
              updateMemberData.services,
              projectMemberProductIds
            ),
          },
    };
    if (onSaveData) onSaveData(response);

    sendAnalytics(
      product.on
        ? TRACK_EVENTS.NAME.PRODUCTS_ON
        : TRACK_EVENTS.NAME.PRODUCTS_OFF,
      product.name
    );

    if (productsToUpdate.current.length > 0) {
      const { type, activated } = productsToUpdate.current.shift();
      processProductAccessChange(
        type,
        activated,
        useWriteConstructionApis
          ? updateMemberData.products
          : updateMemberData.services
      );
    } else {
      const productsAccess = useWriteConstructionApis
        ? getMemberProductsAccessesFromProducts(updateMemberData.products)
        : getMemberProductsAccessesFromServices(updateMemberData.services);
      setMemberProductsAccess(productsAccess);

      if (currentUser && currentUser?.id === member.id) {
        // Project products access are not updated right away
        // setTimeout is used to wait a few seconds
        setTimeout(() => {
          doFetchProjectEntitlements(
            buildGetProjectEntitlementsUrl(projectId, useProjectEntitlementsV2)
          );
        }, 4000);
      }
    }

    if (showTurnOffDocsConfirmation) setTurnOffDocsConfirmation(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateMemberData]);

  useEffect(() => {
    if (!updateMemberError) return;

    const product = lastUpdatedProduct.current;
    showNotification({
      status: NOTIFICATION_STATUSES.ERROR,
      message: T.translate(
        product.on ? 'PRODUCT_TURNED_ON_ERROR' : 'PRODUCT_TURNED_OFF_ERROR',
        {
          productName: product.name,
          TryAgainButton: (
            <LinkButton
              onClick={() =>
                handleProductAccessChange(product.type, product.on)
              }
            >
              {T.translate('TRY_AGAIN')}
            </LinkButton>
          ),
        }
      ),
    });

    if (productsToUpdate.current.length > 0) {
      const { type, activated } = productsToUpdate.current.shift();
      processProductAccessChange(type, activated);
    } else {
      const productsAccess = useWriteConstructionApis
        ? getMemberProductsAccessesFromProducts(memberProducts)
        : getMemberProductsAccessesFromServices(memberServices);
      setMemberProductsAccess(productsAccess);
    }

    if (showTurnOffDocsConfirmation) setTurnOffDocsConfirmation(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateMemberError]);

  useEffect(() => {
    if (!removeMemberData) return;

    setTurnOffDocsConfirmation(false);
    latestOnRemoveMember.current(!!removeMemberData);
    showNotification({
      status: NOTIFICATION_STATUSES.SUCCESS,
      message: T.translate('PRODUCT_TURNED_OFF_SUCCESS', {
        productName: products.find((p) => p.type === PRODUCT_IDS.DOCS).name,
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeMemberData]);

  useEffect(() => {
    if (!removeMemberError) return;

    showNotification({
      status: NOTIFICATION_STATUSES.ERROR,
      message: T.translate('REMOVE_MEMBER_ERROR', {
        name: member.name,
        email: member.email,
        TryAgainButton: (
          <LinkButton onClick={handleTurnOffDocsConfirmationClick}>
            {T.translate('TRY_AGAIN')}
          </LinkButton>
        ),
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeMemberError]);

  // Set checkNoProductsAccess when no products are turned on
  useEffect(() => {
    const checkNoProductsAccess =
      currentUser?.id === member?.id &&
      selectedAccessLevel.value === SERVICE_ACCESSES.ADMIN &&
      !Object.values(memberProductsAccess).includes(true);
    setFieldValue('checkNoProductsAccess', checkNoProductsAccess);
  }, [
    currentUser?.id,
    member?.id,
    memberProductsAccess,
    selectedAccessLevel,
    setFieldValue,
  ]);

  useEffect(() => {
    if (!projectEntitlementsData) return;

    if (
      projectProductsUpdated(
        projectEntitlementsData.currentProject.products,
        memberProductsAccess
      )
    ) {
      setCurrentProject(projectEntitlementsData.currentProject);
    } else {
      setTimeout(() => {
        doFetchProjectEntitlements(
          buildGetProjectEntitlementsUrl(projectId, useProjectEntitlementsV2)
        );
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectEntitlementsData]);

  useEffect(() => {
    if (!enableProjectRolesProducts || !selectedRoles) return;

    const productsAccess =
      getMemberProductsAccessesFromProducts(memberProducts);

    setMemberProductsAccess(productsAccess);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableProjectRolesProducts, selectedRoles]);

  return (
    <ProductsSectionView
      accountName={currentAccount?.account_display_name}
      isAccountAdmin={isAccountAdmin}
      isEmsAccount={isEmsAccount}
      isTurningOffDocs={isTurningOffDocs}
      onProductAccessChange={handleProductAccessChange}
      onProductsBannerClose={handleProductsBannerClose}
      onTurnOffDocsConfirmationClick={handleTurnOffDocsConfirmationClick}
      permissionsEditState={permissionsEditState}
      purchasedProducts={purchasedProducts}
      selectedAccessLevel={selectedAccessLevel}
      setExpandedProduct={setExpandedProduct}
      setTurnOffDocsConfirmation={setTurnOffDocsConfirmation}
      showProductsBanner={showProductsBanner}
      showProductsSectionRedesign={showProductsSectionRedesign}
      showTurnOffDocsConfirmation={showTurnOffDocsConfirmation}
      unpurchansedProducts={unpurchansedProducts}
    />
  );
};

ProductsSection.propTypes = {
  isEmsAccount: PropTypes.bool,
  member: userPropType.isRequired,
  onRemove: PropTypes.func,
  onSaveData: PropTypes.func,
  permissionsEditState: PropTypes.bool.isRequired,
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  showProductsSectionRedesign: PropTypes.bool.isRequired,
};

ProductsSection.defaultProps = {
  isEmsAccount: false,
  onRemove: undefined,
  onSaveData: undefined,
};

export default ProductsSection;
